import { apiClient } from '../../../services/api/client';
import { buildApiUrl, getDefaultHeaders } from '../config/api';
import { PaginatedResponse } from '../../../types';
import {
  Site,
  SiteCreateDto,
  SiteCertification,
  Employee,
  EmployeeCreateDto,
  Department,
  DepartmentCreateDto,
  Position,
  PositionCreateDto,
  Role,
  RoleCreateDto,
  SiteType,
  CertificationType,
  EmployeeStatus,
  RoleCategory,
  RoleScope,
} from '../types';

// Sites API
export const sitesApi = {
  getAll: (): Promise<Site[]> =>
    apiClient.get('/sites'),

  getAllPaginated: (page = 0, size = 20): Promise<PaginatedResponse<Site>> =>
    apiClient.get('/sites/paginated', { page, size }),

  getById: (id: number): Promise<Site> =>
    apiClient.get(`/sites/${id}`),

  getByCode: (siteCode: string): Promise<Site> =>
    apiClient.get(`/sites/code/${siteCode}`),

  create: (data: SiteCreateDto): Promise<Site> =>
    apiClient.post('/sites', data),

  update: (id: number, data: SiteCreateDto): Promise<Site> =>
    apiClient.put(`/sites/${id}`, data),

  delete: (id: number): Promise<void> =>
    apiClient.delete(`/sites/${id}`),
};

// Employees API
export const employeesApi = {
  getAll: (): Promise<Employee[]> =>
    apiClient.get('/employees'),

  getAllPaginated: (page = 0, size = 20): Promise<PaginatedResponse<Employee>> =>
    apiClient.get('/employees/paginated', { page, size }),

  getById: (id: number): Promise<Employee> =>
    apiClient.get(`/employees/${id}`),

  create: (data: EmployeeCreateDto): Promise<Employee> =>
    apiClient.post('/employees', data),

  update: (id: number, data: EmployeeCreateDto): Promise<Employee> =>
    apiClient.put(`/employees/${id}`, data),

  delete: (id: number): Promise<void> =>
    apiClient.delete(`/employees/${id}`),
};

// Departments API
export const departmentsApi = {
  getAll: (): Promise<Department[]> =>
    apiClient.get('/departments'),

  getBySite: (siteId: number): Promise<Department[]> =>
    apiClient.get(`/departments/site/${siteId}`),

  getById: (id: number): Promise<Department> =>
    apiClient.get(`/departments/${id}`),

  create: (data: DepartmentCreateDto): Promise<Department> =>
    apiClient.post('/departments', data),

  update: (id: number, data: DepartmentCreateDto): Promise<Department> =>
    apiClient.put(`/departments/${id}`, data),

  delete: (id: number): Promise<void> =>
    apiClient.delete(`/departments/${id}`),
};

// Positions API
export const positionsApi = {
  getAll: (): Promise<Position[]> =>
    apiClient.get('/positions'),

  getByDepartment: (departmentId: number): Promise<Position[]> =>
    apiClient.get(`/positions/department/${departmentId}`),

  create: (data: PositionCreateDto): Promise<Position> =>
    apiClient.post('/positions', data),

  update: (id: number, data: PositionCreateDto): Promise<Position> =>
    apiClient.put(`/positions/${id}`, data),

  delete: (id: number): Promise<void> =>
    apiClient.delete(`/positions/${id}`),
};

// Roles API - using centralized configuration
export const rolesApi = {
  getAll: async (): Promise<Role[]> => {
    const response = await fetch(buildApiUrl('/roles'), {
      headers: getDefaultHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  getById: async (id: number): Promise<Role> => {
    const response = await fetch(buildApiUrl(`/roles/${id}`), {
      headers: getDefaultHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  getQhseRoles: async (): Promise<Role[]> => {
    const response = await fetch(buildApiUrl('/roles/qhse'), {
      headers: getDefaultHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  create: async (data: RoleCreateDto): Promise<Role> => {
    const response = await fetch(buildApiUrl('/roles'), {
      method: 'POST',
      headers: getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  update: async (id: number, data: RoleCreateDto): Promise<Role> => {
    const response = await fetch(buildApiUrl(`/roles/${id}`), {
      method: 'PUT',
      headers: getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  delete: async (id: number): Promise<void> => {
    const response = await fetch(buildApiUrl(`/roles/${id}`), {
      method: 'DELETE',
      headers: getDefaultHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  },
};

// Reference Data API
export const referenceDataApi = {
  getSiteTypes: (): Promise<SiteType[]> =>
    apiClient.get('/reference/site-types'),

  getCertificationTypes: (): Promise<CertificationType[]> =>
    apiClient.get('/reference/certification-types'),

  getEmployeeStatuses: (): Promise<EmployeeStatus[]> =>
    apiClient.get('/reference/employee-statuses'),

  getRoleCategories: (): Promise<RoleCategory[]> =>
    apiClient.get('/reference/role-categories'),

  getRoleScopes: (): Promise<RoleScope[]> =>
    apiClient.get('/reference/role-scopes'),
};
